'use client'

import { motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMeasure } from 'react-use'
import { IDetailRestaurant } from '@/api/restaurants/types/restaurant-types'
import { Button } from '@/components/button'
import { FadeOverlay } from '@/components/fade-overlay'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { RestaurantDetailMenuGroup } from '@/features/restaurants/restaurant-detail/restaurant-detail-menu/restaurant-detail-menu-group'
import { RestaurantDetailSection } from '@/features/restaurants/restaurant-detail/restaurant-detail-section'
import { RestaurantDetailSectionId } from '@/features/restaurants/restaurant-detail/utils/restaurant-detail-helpers'
import { useMobile } from '@/utils/window/use-mobile'

export interface IRestaurantDetailMenuTranslation {
    title: string
    description: string
}

const MAX_INITIAL_EXPANDED_MENU_GROUPS = 1

export function RestaurantDetailMenu() {
    const t = useTranslations()

    const { restaurant } = useRestaurantDetailContext()

    const getIsInitialExpanded = useCallback((restaurantData: IDetailRestaurant | undefined) => {
        if (!restaurantData?.menuGroups) return false

        return restaurantData.menuGroups.length < MAX_INITIAL_EXPANDED_MENU_GROUPS
    }, [])

    const [isExpanded, setIsExpanded] = useState(() => getIsInitialExpanded(restaurant))

    // If the state is initially expanded, that means we must have already had the data of the restaurant in the first render
    // Hence we don't need to run this effect (which is basically a delayed initial sync of the state with the data)
    const hasInitiallySynced = useRef(isExpanded)
    useEffect(() => {
        if (hasInitiallySynced.current) return

        // Don't sync again, once is enough
        hasInitiallySynced.current = true

        setIsExpanded(getIsInitialExpanded(restaurant))
    }, [restaurant, getIsInitialExpanded])

    const onWindowResize = useCallback(() => {
        if (typeof window === 'undefined') return

        if (window.innerWidth <= 768) {
            setIsExpanded(true)
        }
    }, [])

    const [containerRef, { height: containerHeight }] = useMeasure<HTMLDivElement>()

    useEffect(() => {
        onWindowResize()

        window.addEventListener('resize', onWindowResize)

        return () => window.removeEventListener('resize', onWindowResize)
    }, [onWindowResize])

    const isMobile = useMobile()

    if (!restaurant?.menuGroups?.length) return null

    return (
        <RestaurantDetailSection
            id={RestaurantDetailSectionId.MENU}
            title={'restaurants.detail.menu.title'}
            description={'restaurants.detail.menu.description'}
            className="mb-14"
            withoutBorder={isMobile}
        >
            <motion.div
                initial={{ height: '100%' }}
                animate={{ height: containerHeight || '100%' }}
                transition={{ height: { type: 'tween', duration: 0.05 } }}
                className="overflow-hidden relative"
            >
                <div ref={containerRef} className="flex flex-col gap-y-5">
                    {restaurant.menuGroups?.map((menuGroup, index) => {
                        const isVisible = isExpanded || index < MAX_INITIAL_EXPANDED_MENU_GROUPS

                        if (!isVisible) return null

                        return (
                            <RestaurantDetailMenuGroup
                                currencySymbol={restaurant.city?.country?.currency?.symbol ?? '€'}
                                initialIsCollapsed={false}
                                key={'restaurant-detail-menu' + menuGroup.uuid}
                                menuGroup={menuGroup}
                            />
                        )
                    })}
                </div>

                {!isExpanded && (
                    <FadeOverlay className="hidden md:flex absolute bottom-0 min-h-full" />
                )}
            </motion.div>

            <div className="hidden md:flex items-center justify-center w-full pt-8">
                <Button
                    shape="pill"
                    styleType="secondary"
                    onClick={() => setIsExpanded((prev) => !prev)}
                >
                    {isExpanded
                        ? t('restaurants.detail.menu.showLess')
                        : t('restaurants.detail.menu.showAll')}
                </Button>
            </div>
        </RestaurantDetailSection>
    )
}
